import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Table, Button, message, Input, Typography, Tag, Space, Card, Statistic } from 'antd';
import { ArrowLeftOutlined, FileOutlined, SearchOutlined, FolderOutlined, SaveOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { apiMethods, FileManifestEntry } from '../api';

const { Search } = Input;
const { Text } = Typography;

export default function VersionFiles() {
  const { versionId } = useParams<{ versionId: string }>();
  const navigate = useNavigate();
  const [files, setFiles] = useState<FileManifestEntry[]>([]);
  const [filteredFiles, setFilteredFiles] = useState<FileManifestEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchFiles = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiMethods.getVersionFiles(Number(versionId));
      setFiles(data.files || []);
      setFilteredFiles(data.files || []);
    } catch (error: any) {
      message.error(error.response?.data?.error || '获取文件清单失败');
    } finally {
      setLoading(false);
    }
  }, [versionId]);

  useEffect(() => {
    if (versionId) {
      fetchFiles();
    }
  }, [versionId, fetchFiles]);

  const handleSearch = (value: string) => {
    if (!value) {
      setFilteredFiles(files);
      return;
    }
    const searchLower = value.toLowerCase();
    const filtered = files.filter(f =>
      f.path.toLowerCase().includes(searchLower) ||
      f.checksum.toLowerCase().includes(searchLower)
    );
    setFilteredFiles(filtered);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (path: string) => {
    if (path.startsWith('mods/')) return <FileOutlined style={{ color: '#722ed1' }} />;
    if (path.startsWith('config/')) return <FolderOutlined style={{ color: '#fa8c16' }} />;
    if (path.endsWith('.jar')) return <FileOutlined style={{ color: '#52c41a' }} />;
    return <FileOutlined style={{ color: '#8c8c8c' }} />;
  };

  const getFileTypeTag = (path: string) => {
    if (path.startsWith('mods/')) return <Tag color="purple">Mod</Tag>;
    if (path.startsWith('config/')) return <Tag color="orange">Config</Tag>;
    if (path.endsWith('.json')) return <Tag color="blue">JSON</Tag>;
    if (path.endsWith('.jar')) return <Tag color="green">JAR</Tag>;
    return <Tag color="default">File</Tag>;
  };

  // 统计信息
  const stats = {
    totalFiles: files.length,
    totalSize: files.reduce((sum, f) => sum + f.size, 0),
    modsCount: files.filter(f => f.path.startsWith('mods/')).length,
    configCount: files.filter(f => f.path.startsWith('config/')).length,
  };

  const columns: ColumnsType<FileManifestEntry> = [
    {
      title: '文件路径',
      dataIndex: 'path',
      key: 'path',
      render: (path: string) => (
        <Space>
          {getFileIcon(path)}
          <Text copyable={{ text: path }}>{path}</Text>
        </Space>
      ),
    },
    {
      title: '类型',
      dataIndex: 'path',
      key: 'type',
      width: 100,
      render: (path: string) => getFileTypeTag(path),
    },
    {
      title: '大小',
      dataIndex: 'size',
      key: 'size',
      width: 120,
      render: (size: number) => <Text code>{formatFileSize(size)}</Text>,
      sorter: (a, b) => a.size - b.size,
    },
    {
      title: 'SHA256',
      dataIndex: 'checksum',
      key: 'checksum',
      width: 100,
      render: (checksum: string) => (
        <Text copyable={{ text: checksum }} ellipsis style={{ maxWidth: 120 }}>
          {checksum.slice(0, 8)}...
        </Text>
      ),
    },
  ];

  return (
    <div style={{ padding: isMobile ? 12 : 24 }}>
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate(-1)}
        style={{ marginBottom: 16 }}
      >
        返回
      </Button>

      <Space direction="vertical" style={{ width: '100%' }} size="large">
        {/* 统计卡片 */}
        <Card>
          <Space wrap style={{ width: '100%', justifyContent: isMobile ? 'space-between' : 'flex-start' }}>
            <Statistic
              title={<Text type="secondary"><FileOutlined /> 总文件数</Text>}
              value={stats.totalFiles}
              suffix="个"
            />
            <Statistic
              title={<Text type="secondary"><SaveOutlined /> 总大小</Text>}
              value={formatFileSize(stats.totalSize)}
            />
            <Statistic
              title={<Text type="secondary">Mods</Text>}
              value={stats.modsCount}
              suffix="个"
            />
            <Statistic
              title={<Text type="secondary">Configs</Text>}
              value={stats.configCount}
              suffix="个"
            />
          </Space>
        </Card>

        {/* 搜索框 */}
        <Card>
          <Search
            placeholder="搜索文件路径或 checksum..."
            allowClear
            enterButton={<SearchOutlined />}
            size="large"
            onSearch={handleSearch}
            onChange={(e) => !e.target.value && handleSearch('')}
            style={{ maxWidth: 500 }}
          />
        </Card>

        {/* 文件列表 */}
        <Card title={<Space><FileOutlined /> 文件清单 ({filteredFiles.length})</Space>}>
          <Table
            columns={columns}
            dataSource={filteredFiles}
            loading={loading}
            rowKey="checksum"
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showTotal: (total) => `共 ${total} 个文件`,
            }}
            size="small"
          />
        </Card>
      </Space>
    </div>
  );
}
